import Contact from "@/pages/Contact/Contact";
import LegalCenter from "@/pages/LegalCenterPage/LegalCenter";
import Faq from "@/pages/LegalCenterPage/components/Faq";
import Privacy from "@/pages/LegalCenterPage/components/Privacy";
import Tos from "@/pages/LegalCenterPage/components/Tos";
import { Navigate, Route, Routes, useLocation } from "react-router";

import NewsPage from "./components/features/News/NewsPage/NewsPage";
import AppWrapper from "./components/molecules/AppWrapper/AppWrapper";
import NavigationBar from "./components/organisms/NavigationBar/NavigationBar";
import Components from "./pages/Components/Components";
import { DetailedPlayer } from "./pages/DetailedPlayer/DetailedPlayer";
import BotPrivacy from "./pages/DiscordBot/Privacy";
import BotTos from "./pages/DiscordBot/Tos";
import EmailUnsubscribe from "./pages/EmailUnsubscribe/EmailUnsubscribe";
import EvolutionsPage from "./pages/EvolutionsPage/EvolutionsPage";
import ImportPlayers from "./pages/ImportPlayers/ImportPlayers";
import { ImportPlayersViewSafari } from "./pages/ImportPlayers/views/ImportPlayersViewSafari";
import { MyEvolutionsLandingPage } from "./pages/LandingPages/Features/MyEvolutionsLandingPage";
import { MyPlayersLandingPage } from "./pages/LandingPages/Features/MyPlayersLandingPage";
import { SBCSolverLandingPage } from "./pages/LandingPages/Features/SBCSolverLandingPage";
import LandingPageBasic from "./pages/LandingPages/LandingPageBasic";
import ConfirmLogin from "./pages/Login/ConfirmLogin";
import Login from "./pages/Login/Login";
import MetaRatingLandingPage from "./pages/MetaRatingLandingPage/MetaRatingLandingPage";
import { MyClub } from "./pages/MyClub/MyClub";
import { MyClubPlayers } from "./pages/MyClub/Players/MyClubPlayers";
import News from "./pages/News/News";
import PaymentSuccess from "./pages/Payments/PaymentSuccess";
import PlayersPage from "./pages/Players/PlayersPage";
import Profile from "./pages/Profile/Profile";
import { SBCPageAsContentPostDataWrapper } from "./pages/SBCPageAsContentPost/SBCPageAsContentPostDataWrapper";
import SbcSetPage from "./pages/Sbcs/SbcSetPage/SbcSetPage";
import SbcSetsPage from "./pages/Sbcs/SbcSetsPage";
import SbcSolutionPage from "./pages/Sbcs/SbcSolutionPage";
import SquadBuilder from "./pages/SquadBuilder/SquadBuilder";
import Subscription from "./pages/Subscription/Subscription";
import { SweepStakesTos } from "./pages/SweepStakesTos/SweepStakesTos";
import { TacticsPage } from "./pages/Tactics/TacticsPage";
import WhoAreWe from "./pages/WhoAreWe/WhoAreWe";
import { User } from "./redux/user/userSlice";

interface AppRoutesProps {
  user: User;
}

const AppRoutes = ({ user }: AppRoutesProps) => {
  const location = useLocation();
  return (
    <AppWrapper isLoggedIn={!!user.uuid}>
      <NavigationBar currentRoute={location.pathname} />
      <Routes>
        <Route path={"/profile"} element={<Profile />} />
        <Route path={"/login"} element={<Login />} />
        <Route path={"confirm-login"} element={<ConfirmLogin />} />
        <Route path={"/"} element={<SbcSetsPage />} />
        <Route path={"/squad-builder"} element={<SquadBuilder />}>
          <Route path={":name/:resourceId"} element={<DetailedPlayer />} />
          <Route
            path={":name/:version/:resourceId"}
            element={<DetailedPlayer />}
          />
        </Route>
        <Route
          path={"/squad-builder/best-formation"}
          element={<SquadBuilder />}
        />
        <Route path={"/players"} element={<PlayersPage />}>
          <Route path={":name/:resourceId"} element={<DetailedPlayer />} />
          <Route
            path={":name/:version/:resourceId"}
            element={<DetailedPlayer />}
          />
        </Route>
        <Route path={"/tactics"} element={<TacticsPage />} />
        <Route path={"/evolutions"} element={<EvolutionsPage />}>
          <Route path={":name/:resourceId"} element={<DetailedPlayer />} />
          <Route
            path={":name/:version/:resourceId"}
            element={<DetailedPlayer />}
          />
        </Route>
        <Route path={"/evolutions/my-evolutions"} element={<EvolutionsPage />}>
          <Route path={":name/:resourceId"} element={<DetailedPlayer />} />
          <Route
            path={":name/:version/:resourceId"}
            element={<DetailedPlayer />}
          />
        </Route>
        <Route path={"/sbc-solution/:name/:setId"} element={<SbcSetPage />} />
        <Route
          path={"/sbc-solution/:setName/:setId/:sbcName/:sbcId"}
          element={<SbcSolutionPage />}
        />
        <Route path={"/eafcassistant/tos"} element={<BotTos />} />
        <Route path={"/eafcassistant/privacy"} element={<BotPrivacy />} />
        <Route path={"/who-are-we"} element={<WhoAreWe />} />

        <Route path={"/contact"} element={<Contact />} />
        <Route path={"/sweepstakes-tos"} element={<SweepStakesTos />} />
        <Route path={"/legal"} element={<LegalCenter />}>
          <Route index element={<Privacy />} />
          <Route path="faq" element={<Faq />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="tos" element={<Tos />} />
        </Route>
        <Route path={"/payment-success"} element={<PaymentSuccess />} />
        <Route
          path={"/subscription"}
          element={
            <Subscription
              subscription={user.subscription}
              isLoggedIn={!!user.uuid}
            />
          }
        />
        <Route path={"/import"} element={<ImportPlayers />} />
        <Route path={"/import-mobile"} element={<ImportPlayersViewSafari />} />
        <Route path={"get-my-players"} element={<MyPlayersLandingPage />} />
        <Route
          path={"get-my-evolutions"}
          element={<MyEvolutionsLandingPage />}
        />
        <Route path={"get-sbc-solver"} element={<SBCSolverLandingPage />} />
        <Route path={"/my-club"} element={<MyClub />}>
          <Route path={"players"} element={<MyClubPlayers />} />
          <Route path={":name/:resourceId"} element={<DetailedPlayer />} />
          <Route
            path={":name/:version/:resourceId"}
            element={<DetailedPlayer />}
          />
        </Route>
        <Route path={"meta-rating"} element={<MetaRatingLandingPage />} />
        <Route path={"/news"} element={<News />} />
        <Route path={"/news/:newsLink"} element={<NewsPage />} />
        <Route
          path={"/sbc-page-as-content-post/:id"}
          element={<SBCPageAsContentPostDataWrapper />}
        />
        <Route
          path={"*"}
          element={
            <main className="z-10 mt-8 text-2xl text-center text-white">
              The requested page was not found
            </main>
          }
        />

        <Route
          path={"promo-10"}
          element={
            <LandingPageBasic
              discountText={"Get 10% off first month*"}
              discountId={import.meta.env.VITE_APP_PROMO_10_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"promo-50"}
          element={
            <LandingPageBasic
              discountText={"Get 50% off first month*"}
              discountId={import.meta.env.VITE_APP_PROMO_50_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"youtube-campaign"}
          element={
            <LandingPageBasic
              discountText={"Get 50% off first month*"}
              discountId={
                import.meta.env.VITE_APP_YOUTUBE_CAMPAIGN_DISCOUNT_CODE
              }
            />
          }
        />
        <Route
          path={"andy1"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month with AndyJFC*"
              discountId={import.meta.env.VITE_APP_ANDYJFC_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"andy"}
          element={
            <Navigate
              to="/andy1?utm_source=andy&utm_medium=social&utm_campaign=affiliate"
              replace
            />
          }
        />
        <Route
          path={"futmentor1"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month with FUT Mentor*"
              discountId={import.meta.env.VITE_APP_FUT_MENTOR_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"futmentor"}
          element={
            <Navigate
              to="/futmentor1?utm_source=futmentor&utm_medium=social&utm_campaign=affiliate"
              replace
            />
          }
        />
        <Route
          path={"futpepi1"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month with FUT Pepi Trading*"
              discountId={import.meta.env.VITE_APP_FUT_PEPI_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"futpepi"}
          element={
            <Navigate
              to="/futpepi1?utm_source=futpepi&utm_medium=social&utm_campaign=affiliate&via=futpepitrading"
              replace
            />
          }
        />
        <Route
          path={"futlovers1"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month with Futlovers*"
              discountId={import.meta.env.VITE_APP_FUTLOVERS_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"futlovers"}
          element={
            <Navigate
              to="/futlovers1?utm_source=futlovers&utm_medium=social&utm_campaign=affiliate"
              replace
            />
          }
        />
        <Route
          path={"fbeafc24"}
          element={
            <Navigate
              to="/fbeafc241?utm_source=fbeafc24&utm_medium=social&utm_campaign=affiliate&via=fbeafc24"
              replace
            />
          }
        />
        <Route
          path={"fbeafc241"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month*"
              discountId={import.meta.env.VITE_APP_FBEAFC24_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"icon"}
          element={
            <Navigate
              to="/icon1?utm_source=iconsquadgaming&utm_medium=social&utm_campaign=affiliate&via=icon"
              replace
            />
          }
        />
        <Route
          path={"icon1"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month with IconSquadGaming*"
              discountId={import.meta.env.VITE_APP_ICON_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"hyxer"}
          element={
            <Navigate
              to="/hyxer1?utm_source=hyxer&utm_medium=social&utm_campaign=affiliate&via=hyxer"
              replace
            />
          }
        />
        <Route
          path={"hyxer1"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month with Hyxer*"
              discountId={import.meta.env.VITE_APP_HYXER_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"futworkshop"}
          element={
            <Navigate
              to="/futworkshop1?utm_source=futworkshop&utm_medium=social&utm_campaign=affiliate&via=futworkshop"
              replace
            />
          }
        />
        <Route
          path={"futworkshop1"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month with FutWorkShop*"
              discountId={import.meta.env.VITE_APP_FUTWORKSHOP_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"pablo"}
          element={
            <Navigate
              to="/pablo1?utm_source=pablo&utm_medium=social&utm_campaign=affiliate"
              replace
            />
          }
        />
        <Route
          path={"pablo1"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month with Pablo*"
              discountId={import.meta.env.VITE_APP_PABLO_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"chem241"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month with Chem Expert*"
              discountId={import.meta.env.VITE_APP_CHEM24_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"chem24"}
          element={
            <Navigate
              to="/chem241?utm_source=chem24&utm_medium=social&utm_campaign=affiliate&via=chem24"
              replace
            />
          }
        />
        <Route
          path={"junior1"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month with SkillingJunior*"
              discountId={import.meta.env.VITE_APP_JUNIOR_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"junior"}
          element={
            <Navigate
              to="/junior1?utm_source=junior&utm_medium=social&utm_campaign=affiliate&via=junior"
              replace
            />
          }
        />
        <Route
          path={"david1"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month with David|FIFAllstars*"
              discountId={import.meta.env.VITE_APP_DAVID_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"david"}
          element={
            <Navigate
              to="/david1?utm_source=david&utm_medium=social&utm_campaign=affiliate&via=david"
              replace
            />
          }
        />
        <Route
          path={"einsfut1"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month with EinsFUT*"
              discountId={import.meta.env.VITE_APP_EINSFUT_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"einsfut"}
          element={
            <Navigate
              to="/einsfut1?utm_source=einsfut&utm_medium=social&utm_campaign=affiliate&via=einsfut"
              replace
            />
          }
        />
        <Route
          path={"fccentralen1"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month with FC Centralen*"
              discountId={import.meta.env.VITE_APP_FCCENTRALEN_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"fccentralen"}
          element={
            <Navigate
              to="/fccentralen1?utm_source=fccentralen&utm_medium=social&utm_campaign=affiliate&via=fccentralen"
              replace
            />
          }
        />
        <Route
          path={"rambler"}
          element={
            <Navigate
              to="/rambler1?utm_source=rambler&utm_medium=social&utm_campaign=affiliate&via=rambler"
              replace
            />
          }
        />
        <Route
          path={"rambler1"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month with FUT Rambler*"
              discountId={import.meta.env.VITE_APP_RAMBLER_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"tuga810"}
          element={
            <Navigate
              to="/tuga8101?utm_source=tuga810&utm_medium=social&utm_campaign=affiliate&via=tuga810"
              replace
            />
          }
        />
        <Route
          path={"tuga8101"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month with Tuga810*"
              discountId={import.meta.env.VITE_APP_TUGA810_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"bullardiniho1"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month with Bullardiniho*"
              discountId={import.meta.env.VITE_APP_BULLARDINIHO_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"bullardiniho"}
          element={
            <Navigate
              to="/bullardiniho1?utm_source=bullardiniho&utm_medium=social&utm_campaign=affiliate&via=bullardiniho"
              replace
            />
          }
        />
        <Route
          path={"mentor1"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month with Mentor*"
              discountId={import.meta.env.VITE_APP_MENTOR_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"mentor"}
          element={
            <Navigate
              to="/mentor1?utm_source=mentor&utm_medium=social&utm_campaign=affiliate&via=mentor"
              replace
            />
          }
        />
        <Route
          path={"alexion"}
          element={
            <Navigate
              to="/alexion1?utm_source=alexion&utm_medium=social&utm_campaign=affiliate&via=alexion"
              replace
            />
          }
        />
        <Route
          path={"alexion1"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month with ALEXION*"
              discountId={import.meta.env.VITE_APP_ALEXION_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"gdisplay"}
          element={
            <Navigate
              to="/gdisplay1?utm_source=googledisplay&utm_medium=cpc&utm_campaign=googledisplay&via=googledisplay"
              replace
            />
          }
        />
        <Route
          path={"gdisplay1"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month*"
              discountId={import.meta.env.VITE_APP_GOOGLE_DISPLAY_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"SAF"}
          element={
            <Navigate
              to="/?utm_source=saf&utm_medium=social&utm_campaign=saf"
              replace
            />
          }
        />
        <Route path={"/email-unsubscribe"} element={<EmailUnsubscribe />} />
        <Route
          path={"tekkz"}
          element={
            <LandingPageBasic
              discountText="50% off first month with 50OFFTEKKZ"
              discountId={import.meta.env.VITE_APP_50_OFF_TEKKZ}
            />
          }
        />
        <Route
          path={"promo-tekkz"}
          element={
            <Navigate
              to="/tekkz?utm_source=tekkz&utm_medium=social&utm_campaign=sponsor&via=tekkz"
              replace
            />
          }
        />
        <Route
          path={"promo-meta-50-a"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month*"
              discountId={import.meta.env.VITE_APP_META_A_DISCOUNT_CODE}
            />
          }
        />
        <Route
          path={"promo-meta-50-b"}
          element={
            <LandingPageBasic
              discountText="Get 50% off first month*"
              discountId={import.meta.env.VITE_APP_META_B_DISCOUNT_CODE}
            />
          }
        />

        {/* For showcasing components*/}
        {import.meta.env.VITE_APP_SHOW_COMPONENTS && (
          <Route path={"/components"} element={<Components />} />
        )}
      </Routes>
    </AppWrapper>
  );
};

export default AppRoutes;
